import { ShellGuides, type GuidesIdentity } from '@opengov/analytics/shell';
import { useCallback, useMemo, type ErrorInfo } from 'react';
import { ErrorBoundary } from 'react-error-boundary';
import { useEntity } from '../../contexts/EntityContext';
import { useAuth } from '../../hooks/useAuth';
import { useOgAnalyticsEnabled } from '../../hooks/useOgAnalyticsEnabled';
import { useLoggerWithContext } from '../../services/logger/loggerContext';

/**
 * OG Guides (walkthroughs, tooltips, banners, the Resource Center, What's New)
 * from the Guides Studio, on every PSP page — the shell's own and every mounted
 * module's. Mount it once, in the shell layout; a module does nothing.
 *
 * Renders nothing itself: the SDK loads the guides runtime from the Guides API
 * and it draws in its own shadow root. Guide events go to PSP's shared
 * analytics client, and the SDK waits until that client exists.
 *
 * Read here, creating nothing:
 * - `VITE_GUIDES_API_URL` and `VITE_GUIDES_APP_KEY` — the Guides API and PSP's
 *   application key in the Studio. Either unset or empty (an env file line
 *   such as `VITE_GUIDES_APP_KEY=` gives `''`): nothing is rendered here, so
 *   the SDK is never mounted and the runtime is never downloaded.
 * - `useOgAnalyticsEnabled` — true only when the shared analytics client
 *   exists: the `psp-og-analytics` flag is on and client creation succeeded
 *   (it does not when storage is blocked, or the mode has no key). Otherwise
 *   the SDK would wait for a client that will not come.
 *
 * Identity is the Auth0 access token, verified by the Guides API against
 * Auth0's keys (the application's Identity settings in the Studio). The SDK
 * has no unsigned dev mode, and sends the shell's active entity as the account.
 *
 * Wrapped in its own error boundary, like `PspAnnouncements`: guides that fail
 * to render disappear and are logged, and never take the page with them.
 */
export function PspGuides() {
  const apiBaseUrl = import.meta.env.VITE_GUIDES_API_URL;
  const appKey = import.meta.env.VITE_GUIDES_APP_KEY;
  const logger = useLoggerWithContext();
  const onBoundaryError = useCallback(
    (error: unknown, info: ErrorInfo) => {
      logger.error('Guides failed to render', {
        error: error instanceof Error ? error.message : String(error),
        componentStack: info.componentStack,
      });
    },
    [logger]
  );

  if (!apiBaseUrl || !appKey) return null;

  return (
    <ErrorBoundary fallback={null} onError={onBoundaryError}>
      <PspGuideRuntime apiBaseUrl={apiBaseUrl} appKey={appKey} />
    </ErrorBoundary>
  );
}

function PspGuideRuntime({ apiBaseUrl, appKey }: { apiBaseUrl: string; appKey: string }) {
  const logger = useLoggerWithContext();
  const analyticsOn = useOgAnalyticsEnabled();
  const { activeEntity } = useEntity();
  const { getAccessTokenSilently } = useAuth();

  const identity: GuidesIdentity = useMemo(
    () => ({ mode: 'jwt', getAccessToken: () => getAccessTokenSilently() }),
    [getAccessTokenSilently]
  );
  const onError = useCallback(
    (error: Error) => logger.warn('Guides runtime failed to load', { error: error.message }),
    [logger]
  );

  return (
    <ShellGuides
      apiBaseUrl={apiBaseUrl}
      appKey={appKey}
      identity={identity}
      enabled={analyticsOn}
      // The same segment `useOgAnalytics` collapses, so Studio Pages and
      // PostHog routes are both `/general-ledger/:entity/...`.
      collapseSegment={activeEntity?.friendlyId}
      onError={onError}
    />
  );
}
